import React, { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";

import { MarkdownContainer } from "../../../components/markdown/MarkdownContent";
import { ATTACHMENT_LABEL_PREFIX } from "../../../components/markdown/markdownAttachment";
import Button from "../../../components/button/Button";
import ToolTip from "../../../components/tooltip/ToolTip";
import DateInput from "../../../components/calendar/DateInput";
import Input from "../../../components/form-item/Input";
import Toggle from "../../../components/form-item/Toggle";
import { useInitiateUploadMutation } from "../../../app/services/uploadApi";
import type { NoteContentStyle } from "../../../app/services/notes";
import { uploadNoteFile } from "../utils/noteFileUpload";
import {
  normalizeNoteMarkdown,
  normalizePastedMarkdown,
} from "../utils/markdown";
import { buildNoteEditorExtensions } from "./noteEditorExtensions";

/**
 * ノートの WYSIWYG エディタ（Notion / Obsidian 風）。
 *
 * - Tiptap（ProseMirror）でブロック編集し、保存形式は従来どおり Markdown
 *   （tiptap-markdown で相互変換。既存の表示・wikilink・インポートはそのまま）
 * - ソースモード切替で生の Markdown も直接編集できる（Obsidian のソースモード相当）
 * - ファイルはツールバー・ドラッグ&ドロップ・貼り付けで purpose=note アップロード
 *   （画像は画像として埋め込み、その他はダウンロードリンクとして挿入）
 * - `[[ノートタイトル]]` はテキストとして保持され、閲覧時にリンク解決される
 * - 改行（Shift+Enter / 貼り付けの単一改行）は素の改行として保存する
 *   （閲覧側 MarkdownContent の breaks と対で「改行 = 改行表示」。
 *   行末 `\` の強制改行記法は使わない）
 * - シリアライズが付けるバックスラッシュエスケープ（`\###` `\*\*` `\[\[` 等）は
 *   保存・ソース表示・読み込みの各時点で normalizeNoteMarkdown により復元する
 * - 保存/キャンセルは編集領域下端の右寄せアクションパネル
 *   （オファー作成の WizardActionPanel と同じグラス風デザイン）
 * - ノート/カードはモデル統合済み（display_style）。styleSwitchable 指定で
 *   表示形式の切替 UI を出し、カード表示のときだけカード固有フィールド
 *   （カテゴリ / 有効期限 / 必須表示 / ホームに投稿）を表示する。
 *   保存時は本文と合わせて NoteSavePayload として返す
 */

const EditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* 閲覧（NoteView）と同じ行長に揃える */
  width: min(720px, 100% - 32px);
  margin: 0 auto;
  padding: 16px 0 32px;
`;

/**
 * 保存/キャンセルのアクションパネル。オファー作成（WizardActionPanel）と
 * 同じグラスモーフィズムで、編集領域の下端に sticky で追従し、
 * キャンセルは左端・保存系ボタンは右側にまとめる。埋め込み表示
 * （プロジェクト内タブ）では BottomAppBar の表示/非表示
 * （スクロール方向連動）に合わせて縦位置を最適化する
 * （DM の ActionPanel と同じ挙動）。
 */
const EditorActionPanel = styled.div.attrs({
  className: "u-row u-gap8 u-items-center",
})<{ $bottom: number }>`
  position: sticky;
  bottom: ${({ $bottom }) => $bottom}px;
  z-index: 3;
  justify-content: space-between;
  padding: 8px 12px;
  transition: bottom 0.15s linear;

  /* グラスモーフィズム（OfferWizardOverlay の WizardActionPanel と統一） */
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);
`;

/** BottomAppBar の高さ（--bottom-app-bar-height と同値） */
const BOTTOM_BAR_HEIGHT = 80;
/** パネルと画面下端 / BottomAppBar 上端との隙間 */
const PANEL_BOTTOM_GAP = 16;

const TitleInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  font-size: 18px;
  line-height: 1.5;
  outline: none;

  &:focus {
    border-color: var(--soremo-blue);
  }

  &::placeholder {
    color: var(--soremo-light-gray);
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  padding: 4px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fcfcfc;
  position: sticky;
  top: 8px;
  z-index: 2;
`;

const ToolbarButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: ${({ $active }) => ($active ? "#eaf6fb" : "transparent")};
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 20px;
    color: ${({ $active }) =>
      $active ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background: var(--soremo-background);

      .material-symbols-rounded {
        color: var(--soremo-blue);
      }
    }
  }
`;

const ToolbarDivider = styled.span`
  width: 1px;
  height: 20px;
  background: var(--soremo-border);
  margin: 0 4px;
  flex-shrink: 0;
`;

const ToolbarSpacer = styled.span`
  flex: 1;
`;

/**
 * 表の中にカーソルがあるときだけ出す行/列の操作バー。
 * Material Symbols に「行/列を削除」に相当するアイコンが無いため、
 * アイコン + ラベルのチップ形式にして SP でも押し分けられるようにする。
 */
const TableToolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 8px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: var(--soremo-background);
`;

const TableToolbarButton = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid var(--soremo-border);
  border-radius: 14px;
  background: #fff;
  color: ${({ $danger }) =>
    $danger ? "var(--soremo-gray)" : "var(--soremo-blue)"};
  cursor: pointer;
  white-space: nowrap;

  .material-symbols-rounded {
    font-size: 16px;
    color: inherit;
  }

  @media (hover: hover) {
    &:hover {
      border-color: currentColor;
    }
  }
`;

/**
 * 編集面。閲覧時と同じタイポグラフィ（MarkdownContainer の document バリアント）
 * を土台に、ProseMirror / Tiptap 固有の見た目（タスクリスト・テーブル選択・
 * placeholder）を追加する。
 */
const EditorSurface = styled(MarkdownContainer).attrs({
  $variant: "document" as const,
})`
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  padding: 12px;
  background: #fff;
  cursor: text;

  .ProseMirror {
    outline: none;
    min-height: 420px;
  }

  .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    color: var(--soremo-light-gray);
    float: left;
    height: 0;
    pointer-events: none;
  }

  /* Tiptap TaskList（ul[data-type="taskList"]）の見た目を既存タスクリストに揃える */
  ul[data-type="taskList"] {
    list-style: none;
    padding-left: 0;
    margin: 4px 0;

    li {
      display: flex;
      align-items: flex-start;
      gap: 4px;
      margin: 4px 0;

      > label {
        flex-shrink: 0;
        margin: 0;
      }

      > div {
        flex: 1;
        min-width: 0;
      }
    }

    input[type="checkbox"] {
      appearance: none;
      -webkit-appearance: none;
      width: 24px;
      height: 24px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin: 0;
    }

    input[type="checkbox"]::before {
      content: "check_box_outline_blank";
      font-family: "Material Symbols Rounded";
      font-size: 24px;
      color: var(--soremo-light-gray);
    }

    input[type="checkbox"]:checked::before {
      content: "select_check_box";
    }
  }

  /* テーブル編集時はセル境界を明示し、空のセルでもタップで狙えるようにする
     （閲覧側は横罫のみの読み物スタイル、編集側は方眼のグリッド） */
  .ProseMirror table {
    margin: 8px 0;
  }

  /* 空のセルでも幅が潰れずタップで狙える大きさを保つ（列幅の決まり方は
     閲覧側と同じ auto レイアウトのまま） */
  .ProseMirror th,
  .ProseMirror td {
    min-width: 4em;
    padding: 6px 8px;
    border: 1px solid var(--soremo-border);
    vertical-align: top;
  }

  /* セル内の段落は下マージン（document バリアントの 0.8em）を持たせない */
  .ProseMirror th > *,
  .ProseMirror td > * {
    margin: 0;
  }

  .ProseMirror .selectedCell {
    background: rgba(0, 154, 206, 0.08);
  }

  .ProseMirror img {
    max-width: 100%;
  }

  .ProseMirror img.ProseMirror-selectednode {
    outline: 2px solid var(--soremo-blue);
    border-radius: 4px;
  }
`;

const SourceTextarea = styled.textarea`
  width: 100%;
  min-height: 480px;
  padding: 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  font-size: 13px;
  font-family: monospace;
  line-height: 1.6;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: var(--soremo-blue);
  }
`;

/* カード固有フィールドの枠付きボックス（utility に該当がないため局所 styled） */
const FieldsBox = styled.div.attrs({
  className: "u-col u-gap16",
})`
  align-items: stretch;
  padding: 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fcfcfc;
`;

/** 表示形式（ノート / カード）のセグメント切替 */
const StyleSwitch = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--soremo-border);
  border-radius: 16px;
  overflow: hidden;
`;

const StyleSwitchButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 12px;
  border: none;
  background: ${({ $active }) => ($active ? "#eaf6fb" : "#fff")};
  color: ${({ $active }) =>
    $active ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
  cursor: pointer;
  white-space: nowrap;

  .material-symbols-rounded {
    font-size: 16px;
    color: inherit;
  }

  @media (hover: hover) {
    &:hover {
      color: var(--soremo-blue);
    }
  }
`;

interface ToolbarActionDef {
  icon: string;
  title: string;
  isActive?: (editor: Editor) => boolean;
  run: (editor: Editor) => void;
}

const TOOLBAR_ACTIONS: (ToolbarActionDef | "divider")[] = [
  {
    icon: "format_h1",
    title: "見出し1",
    isActive: (e) => e.isActive("heading", { level: 1 }),
    run: (e) => e.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    icon: "format_h2",
    title: "見出し2",
    isActive: (e) => e.isActive("heading", { level: 2 }),
    run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    icon: "format_h3",
    title: "見出し3",
    isActive: (e) => e.isActive("heading", { level: 3 }),
    run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  "divider",
  {
    icon: "format_bold",
    title: "太字",
    isActive: (e) => e.isActive("bold"),
    run: (e) => e.chain().focus().toggleBold().run(),
  },
  {
    icon: "format_italic",
    title: "斜体",
    isActive: (e) => e.isActive("italic"),
    run: (e) => e.chain().focus().toggleItalic().run(),
  },
  {
    icon: "strikethrough_s",
    title: "取り消し線",
    isActive: (e) => e.isActive("strike"),
    run: (e) => e.chain().focus().toggleStrike().run(),
  },
  {
    icon: "code",
    title: "インラインコード",
    isActive: (e) => e.isActive("code"),
    run: (e) => e.chain().focus().toggleCode().run(),
  },
  "divider",
  {
    icon: "format_list_bulleted",
    title: "箇条書き",
    isActive: (e) => e.isActive("bulletList"),
    run: (e) => e.chain().focus().toggleBulletList().run(),
  },
  {
    icon: "format_list_numbered",
    title: "番号付きリスト",
    isActive: (e) => e.isActive("orderedList"),
    run: (e) => e.chain().focus().toggleOrderedList().run(),
  },
  {
    icon: "checklist",
    title: "チェックリスト",
    isActive: (e) => e.isActive("taskList"),
    run: (e) => e.chain().focus().toggleTaskList().run(),
  },
  {
    icon: "format_quote",
    title: "引用",
    isActive: (e) => e.isActive("blockquote"),
    run: (e) => e.chain().focus().toggleBlockquote().run(),
  },
  {
    icon: "code_blocks",
    title: "コードブロック",
    isActive: (e) => e.isActive("codeBlock"),
    run: (e) => e.chain().focus().toggleCodeBlock().run(),
  },
  "divider",
  {
    icon: "table",
    title: "テーブルを挿入",
    run: (e) =>
      e
        .chain()
        .focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run(),
  },
  {
    icon: "horizontal_rule",
    title: "区切り線",
    run: (e) => e.chain().focus().setHorizontalRule().run(),
  },
];

interface TableActionDef {
  icon: string;
  label: string;
  run: (editor: Editor) => void;
  /** 削除系（配色を控えめにする） */
  danger?: boolean;
}

/**
 * 表の行/列の操作。Tab（次のセル・末尾で行追加）はキーボードのある環境
 * でしか使えないため、SP でも同じことができるようボタンで用意する。
 *
 * セル結合は Markdown（GFM）で表現できず HTML へフォールバックしてしまう
 * ため、あえて出さない。
 */
const TABLE_ACTIONS: TableActionDef[] = [
  {
    icon: "add_row_above",
    label: "上に行",
    run: (e) => e.chain().focus().addRowBefore().run(),
  },
  {
    icon: "add_row_below",
    label: "下に行",
    run: (e) => e.chain().focus().addRowAfter().run(),
  },
  {
    icon: "add_column_left",
    label: "左に列",
    run: (e) => e.chain().focus().addColumnBefore().run(),
  },
  {
    icon: "add_column_right",
    label: "右に列",
    run: (e) => e.chain().focus().addColumnAfter().run(),
  },
  {
    icon: "delete",
    label: "行を削除",
    run: (e) => e.chain().focus().deleteRow().run(),
    danger: true,
  },
  {
    icon: "delete",
    label: "列を削除",
    run: (e) => e.chain().focus().deleteColumn().run(),
    danger: true,
  },
  {
    icon: "grid_off",
    label: "表を削除",
    run: (e) => e.chain().focus().deleteTable().run(),
    danger: true,
  },
];

/**
 * 保存時にエディタが返す内容。ノート/カード共通（変更提案の payload と同形 +
 * display_style）。カード固有フィールドはノート表示でも既定値のまま保持される。
 */
export interface NoteSavePayload {
  title: string;
  content: string;
  display_style: NoteContentStyle;
  category: string;
  expires_at: string | null;
  is_required: boolean;
  is_published: boolean;
}

/** カード固有メタ + 表示形式の初期値（編集対象 / 変更提案 payload から渡す） */
export interface NoteEditorMeta {
  display_style?: NoteContentStyle;
  category?: string;
  expires_at?: string | null;
  is_required?: boolean;
  is_published?: boolean;
}

export interface NoteEditorProps {
  initialTitle: string;
  initialContent: string;
  /** 表示形式・カードメタの初期値（省略 = ノート表示） */
  initialMeta?: NoteEditorMeta | null;
  /**
   * 表示形式（ノート / カード）の切替 UI を出すか。
   * 変更提案（PR）の編集は target_type が固定のため false にする
   */
  styleSwitchable?: boolean;
  /** 表示形式の切替通知（呼び出し側でボタンラベル等を出し分ける用） */
  onDisplayStyleChange?: (style: NoteContentStyle) => void;
  saving: boolean;
  onSave: (payload: NoteSavePayload) => void;
  onCancel: () => void;
  /** タイトルとツールバーの間に差し込む注意書き等（下書き編集中バナー等） */
  headerFields?: React.ReactNode;
  /** 保存（主アクション）ボタンのラベル */
  saveLabel?: string;
  /** 補助保存アクションのラベル（「更新のみ」等。指定時のみ表示） */
  middleSaveLabel?: string;
  /** 補助保存アクション（middleSaveLabel 指定時に使用） */
  onSaveMiddle?: (payload: NoteSavePayload) => void;
  /**
   * 「下書き保存」アクション（OSS ワークフロー）。指定時のみ表示し、
   * キャンセルの右・他の保存アクションの左に text ボタンで置く。
   */
  draftSaveLabel?: string;
  onSaveDraft?: (payload: NoteSavePayload) => void;
  /**
   * プロジェクト内タブ（embedded）表示か。true のときアクションパネルを
   * 下部ナビ（BottomAppBar）の上に逃がす。
   */
  embedded?: boolean;
}

const NoteEditor: React.FC<NoteEditorProps> = ({
  initialTitle,
  initialContent,
  initialMeta = null,
  styleSwitchable = false,
  onDisplayStyleChange,
  saving,
  onSave,
  onCancel,
  headerFields,
  saveLabel = "保存",
  middleSaveLabel,
  onSaveMiddle,
  draftSaveLabel,
  onSaveDraft,
  embedded = false,
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [mode, setMode] = useState<"rich" | "source">("rich");
  const [sourceContent, setSourceContent] = useState(initialContent);
  const [uploading, setUploading] = useState(false);

  // ── 表示形式 + カード固有メタ ──
  const [displayStyle, setDisplayStyle] = useState<NoteContentStyle>(
    initialMeta?.display_style ?? "note",
  );
  const [category, setCategory] = useState(initialMeta?.category ?? "");
  const [expiresAt, setExpiresAt] = useState<Date | null>(
    initialMeta?.expires_at ? dayjs(initialMeta.expires_at).toDate() : null,
  );
  const [isRequired, setIsRequired] = useState(
    initialMeta?.is_required ?? false,
  );
  const [isPublished, setIsPublished] = useState(
    initialMeta?.is_published ?? false,
  );

  const changeDisplayStyle = useCallback(
    (style: NoteContentStyle) => {
      setDisplayStyle(style);
      onDisplayStyleChange?.(style);
    },
    [onDisplayStyleChange],
  );

  // BottomAppBar の表示/非表示（スクロール方向）に合わせてアクションパネルの
  // 縦位置を追従させる（DM / オファー作成の ActionPanel と同じ方式）。
  // 埋め込み（プロジェクト内タブ）以外は BottomAppBar が無いため固定。
  const scrollState = useSelector((state: { layout: string }) => state.layout);
  const actionPanelBottom = embedded
    ? (scrollState !== "down" ? BOTTOM_BAR_HEIGHT : 0) + PANEL_BOTTOM_GAP
    : PANEL_BOTTOM_GAP;

  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [initiateUpload] = useInitiateUploadMutation();

  // editorProps のハンドラは useEditor 初期化時のクロージャに固定されるため、
  // 最新のアップロード処理・貼り付け処理を ref 経由で参照する。
  const uploadFilesRef = useRef<(files: File[]) => void>(() => {});
  const pasteMarkdownRef = useRef<(text: string) => boolean>(() => false);

  const editor = useEditor({
    // 拡張一式は noteEditorExtensions.ts に集約（Markdown 往復の冪等性
    // テストと共有。Image は inline: true — 詳細はモジュールのコメント）
    extensions: buildNoteEditorExtensions(),
    // 過去の不具合でエスケープされたまま保存された本文（`\###` 等)も、
    // リッチ編集で開いた時点で記法として解釈できる形に復元する（自己修復）
    content: normalizeNoteMarkdown(initialContent),
    editorProps: {
      handleDrop: (_view, event) => {
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (files.length > 0) {
          event.preventDefault();
          uploadFilesRef.current(files);
          return true;
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []);
        if (files.length > 0) {
          event.preventDefault();
          uploadFilesRef.current(files);
          return true;
        }
        // プレーンテキスト貼り付けで、見出し記法のゆれ・エスケープ済み
        // ソースの正規化が必要なときだけ自前で Markdown として挿入する。
        // それ以外は tiptap-markdown（transformPastedText）に委譲する。
        // HTML 付きコピー（リッチコピー）は HTML 側の構造を優先する。
        const text = event.clipboardData?.getData("text/plain") ?? "";
        const html = event.clipboardData?.getData("text/html") ?? "";
        if (text && !html && pasteMarkdownRef.current(text)) {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
  });

  pasteMarkdownRef.current = (text: string): boolean => {
    if (!editor) return false;
    const normalized = normalizePastedMarkdown(text);
    if (normalized === text) return false;
    // tiptap-markdown の clipboardTextParser と同じ経路（markdown-it →
    // HTML → ProseMirror）で、正規化済みテキストを挿入する
    const parsedHtml = editor.storage.markdown.parser.parse(normalized, {
      inline: true,
    }) as string;
    editor.commands.insertContent(parsedHtml, {
      parseOptions: { preserveWhitespace: true },
    });
    return true;
  };

  // リッチ編集のシリアライズは `[[..]]` `###` `**` 等をバックスラッシュで
  // エスケープして記法を壊すため、保存・ソース表示の前に復元する
  // （ソースモードはユーザーが書いた生 Markdown なのでそのまま）。
  const getCurrentMarkdown = useCallback((): string => {
    if (mode === "source") return sourceContent;
    return normalizeNoteMarkdown(editor?.storage.markdown.getMarkdown() ?? "");
  }, [mode, sourceContent, editor]);

  const buildPayload = useCallback(
    (): NoteSavePayload => ({
      title: title.trim(),
      content: getCurrentMarkdown(),
      display_style: displayStyle,
      category: category.trim(),
      // 期限は選択日の終わりまで有効とする
      expires_at: expiresAt
        ? dayjs(expiresAt).endOf("day").format("YYYY-MM-DDTHH:mm:ss")
        : null,
      is_required: isRequired,
      is_published: isPublished,
    }),
    [
      title,
      getCurrentMarkdown,
      displayStyle,
      category,
      expiresAt,
      isRequired,
      isPublished,
    ],
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setUploading(true);
      try {
        for (const file of files) {
          const uploaded = await uploadNoteFile(initiateUpload, file);
          if (file.type.startsWith("image/")) {
            const altText = file.name.replace(/\.[^.]+$/, "");
            if (mode === "rich" && editor) {
              editor
                .chain()
                .focus()
                .setImage({ src: uploaded.url, alt: altText })
                .run();
            } else {
              setSourceContent(
                (prev) => `${prev}\n![${altText}](${uploaded.url})\n`,
              );
            }
          } else {
            // 画像以外はダウンロードリンク（📎 ファイル名）として挿入する。
            // 📎 ラベルは添付カード表示（markdownAttachment）が保存ファイル名の
            // 復元に読み戻すため、プレフィックスは定数を共用する。
            const label = `${ATTACHMENT_LABEL_PREFIX} ${file.name}`;
            if (mode === "rich" && editor) {
              editor
                .chain()
                .focus()
                .insertContent([
                  {
                    type: "text",
                    text: label,
                    marks: [{ type: "link", attrs: { href: uploaded.url } }],
                  },
                  { type: "text", text: " " },
                ])
                .run();
            } else {
              // Markdown リンクを壊さないよう、ファイル名中の角括弧をエスケープする
              const escaped = label.replace(/([[\]])/g, "\\$1");
              setSourceContent(
                (prev) => `${prev}\n[${escaped}](${uploaded.url})\n`,
              );
            }
          }
        }
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : "ファイルのアップロードに失敗しました",
        );
      } finally {
        setUploading(false);
      }
    },
    [initiateUpload, editor, mode],
  );
  uploadFilesRef.current = (files) => void uploadFiles(files);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = "";
      void uploadFiles(files);
    },
    [uploadFiles],
  );

  // WYSIWYG ⇄ Markdown ソースの切り替え（内容は相互変換して引き継ぐ）
  const toggleMode = useCallback(() => {
    if (!editor) return;
    if (mode === "rich") {
      setSourceContent(
        normalizeNoteMarkdown(editor.storage.markdown.getMarkdown() ?? ""),
      );
      setMode("source");
    } else {
      editor.commands.setContent(sourceContent);
      setMode("rich");
    }
  }, [editor, mode, sourceContent]);

  const canSave = title.trim().length > 0 && !saving && !uploading;

  return (
    <EditorWrapper>
      <TitleInput
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={
          displayStyle === "card" ? "カードのタイトル" : "ノートのタイトル"
        }
        maxLength={255}
      />

      {headerFields}

      {styleSwitchable && (
        <div className="u-row u-gap8 u-items-center">
          <span className="bodytext-13 u-text-gray">表示形式</span>
          <StyleSwitch role="radiogroup" aria-label="表示形式">
            <StyleSwitchButton
              type="button"
              role="radio"
              aria-checked={displayStyle === "note"}
              $active={displayStyle === "note"}
              onClick={() => changeDisplayStyle("note")}
            >
              <span className="material-symbols-rounded">description</span>
              <span className="bodytext-13">ノート</span>
            </StyleSwitchButton>
            <StyleSwitchButton
              type="button"
              role="radio"
              aria-checked={displayStyle === "card"}
              $active={displayStyle === "card"}
              onClick={() => changeDisplayStyle("card")}
            >
              <span className="material-symbols-rounded">style</span>
              <span className="bodytext-13">カード</span>
            </StyleSwitchButton>
          </StyleSwitch>
          <span className="bodytext-11 u-text-light-gray">
            {displayStyle === "card"
              ? "カード: 共有TIPS として表示し、ホームのやることリストに投稿できます"
              : "ノート: 読み物として表示します"}
          </span>
        </div>
      )}

      {displayStyle === "card" && (
        <FieldsBox>
          <Input
            label="カテゴリ"
            option="optional"
            value={category}
            maxlength={100}
            placeholder="例: 運用ルール"
            onChange={(e) => setCategory(e.target.value)}
          />

          <div className="u-row u-gap8 u-items-end">
            <DateInput
              label="有効期限"
              option="optional"
              mode="single"
              width="240px"
              value={expiresAt}
              placeholder="無期限"
              minDate={new Date()}
              onChange={(e) =>
                setExpiresAt(
                  e.target.value instanceof Date ? e.target.value : null,
                )
              }
            />
            {expiresAt && (
              <Button
                size="small"
                theme="secondary"
                variant="text"
                width="fit"
                onClick={() => setExpiresAt(null)}
              >
                クリア
              </Button>
            )}
          </div>

          <Toggle
            value={isRequired}
            onClick={(e) => {
              e.preventDefault();
              setIsRequired((v) => !v);
            }}
          >
            <span className="bodytext-13">必須表示</span>
            <span className="bodytext-11 u-text-light-gray u-ml8">
              やることリストの先頭に固定表示する
            </span>
          </Toggle>

          <Toggle
            value={isPublished}
            onClick={(e) => {
              e.preventDefault();
              setIsPublished((v) => !v);
            }}
          >
            <span className="bodytext-13">ホームに投稿</span>
            <span className="bodytext-11 u-text-light-gray u-ml8">
              {isPublished
                ? "投稿する（対象プロジェクトのやることリストに表示されます）"
                : "投稿しない（共有ノート内でのみ閲覧できます）"}
            </span>
          </Toggle>
        </FieldsBox>
      )}

      <Toolbar>
        {mode === "rich" &&
          editor &&
          TOOLBAR_ACTIONS.map((action, i) =>
            action === "divider" ? (
              <ToolbarDivider key={`divider-${i}`} />
            ) : (
              <ToolTip key={action.icon} description={action.title}>
                <ToolbarButton
                  aria-label={action.title}
                  $active={action.isActive?.(editor) ?? false}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => action.run(editor)}
                >
                  <span className="material-symbols-rounded">
                    {action.icon}
                  </span>
                </ToolbarButton>
              </ToolTip>
            ),
          )}
        {mode === "rich" && <ToolbarDivider />}
        <ToolTip description="画像を挿入">
          <ToolbarButton
            aria-label="画像を挿入"
            disabled={uploading}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => imageInputRef.current?.click()}
          >
            <span className="material-symbols-rounded">
              {uploading ? "hourglass_top" : "image"}
            </span>
          </ToolbarButton>
        </ToolTip>
        <ToolTip description="ファイルを添付">
          <ToolbarButton
            aria-label="ファイルを添付"
            disabled={uploading}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
          >
            <span className="material-symbols-rounded">
              {uploading ? "hourglass_top" : "attach_file"}
            </span>
          </ToolbarButton>
        </ToolTip>
        <ToolbarSpacer />
        <ToolTip
          description={
            mode === "rich" ? "Markdownソースを編集" : "リッチ編集に戻る"
          }
        >
          <ToolbarButton
            aria-label="編集モード切り替え"
            $active={mode === "source"}
            onClick={toggleMode}
          >
            <span className="material-symbols-rounded">markdown</span>
          </ToolbarButton>
        </ToolTip>
      </Toolbar>

      {mode === "rich" && editor?.isActive("table") && (
        <TableToolbar>
          <span className="bodytext-11 u-text-light-gray">表の編集</span>
          {TABLE_ACTIONS.map((action) => (
            <TableToolbarButton
              key={action.label}
              type="button"
              $danger={action.danger}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => action.run(editor)}
            >
              <span className="material-symbols-rounded">{action.icon}</span>
              <span className="bodytext-11">{action.label}</span>
            </TableToolbarButton>
          ))}
        </TableToolbar>
      )}

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={handleFileSelect}
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleFileSelect}
      />

      {mode === "rich" ? (
        <EditorSurface onClick={() => editor?.chain().focus().run()}>
          <EditorContent editor={editor} />
        </EditorSurface>
      ) : (
        <SourceTextarea
          value={sourceContent}
          onChange={(e) => setSourceContent(e.target.value)}
          placeholder="Markdown で入力..."
        />
      )}

      <p className="bodytext-11 u-text-light-gray u-line-height-150">
        [[ノートタイトル]] で他のノートへリンクできます。画像やファイルは
        ドラッグ&ドロップ / 貼り付けでも挿入できます（50MB 未満、画像以外は
        ダウンロードリンクとして挿入されます）。表の中では Tab
        で次のセルへ移動し、右下のセルで Tab を押すと行が増えます。
      </p>

      <EditorActionPanel $bottom={actionPanelBottom}>
        <Button
          size="small"
          theme="tertiary"
          variant="filled"
          onClick={onCancel}
        >
          キャンセル
        </Button>
        <div className="u-row u-gap8 u-items-center">
          {draftSaveLabel && onSaveDraft && (
            <Button
              size="small"
              theme="secondary"
              variant="filled"
              onClick={() => onSaveDraft(buildPayload())}
              disabled={!canSave}
            >
              {draftSaveLabel}
            </Button>
          )}
          {middleSaveLabel && onSaveMiddle && (
            <Button
              size="small"
              theme="secondary"
              variant="filled"
              onClick={() => onSaveMiddle(buildPayload())}
              disabled={!canSave}
            >
              {middleSaveLabel}
            </Button>
          )}
          <Button
            size="small"
            theme="primary"
            onClick={() => onSave(buildPayload())}
            disabled={!canSave}
          >
            {saving ? "保存中..." : saveLabel}
          </Button>
        </div>
      </EditorActionPanel>
    </EditorWrapper>
  );
};

export default NoteEditor;
